// src/pages/admin/library/CategoryManagement.tsx
import React, { useState, useEffect, useMemo } from 'react';
import { 
  FaSearch, 
  FaPlus, 
  FaEdit,
  FaTrash,
  FaSync,
  FaUpload,
  FaDownload,
  FaFilter
} from '../../../utils/iconUtils';
import { 
  LibraryCategory, 
  getLibraryCategories, 
  createLibraryCategory,
  updateLibraryCategory,
  deleteLibraryCategory
} from '../../../firebase/libraryFirestoreService';
import { cache } from '../../../utils/cacheManager';
import AddCategoryModal from './AddCategoryModal';

// Cache keys and expiry
const CATEGORIES_CACHE_KEY = 'library.categories';
const CATEGORIES_CACHE_EXPIRY = 30 * 60 * 1000; // 30 minutes (categories don't change often)

const CategoryManagement: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [categories, setCategories] = useState<LibraryCategory[]>([]); // All cached categories
  const [filteredCategories, setFilteredCategories] = useState<LibraryCategory[]>([]); // Filtered for display
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState<LibraryCategory | null>(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;

  // Use centralized cache
  const invalidateCache = (cacheKey: string) => {
    try {
      cache.invalidate(cacheKey);
    } catch (e) {
      // ignore
    }
  };

  // Helpers to inspect persisted cache state and read stale data
  const isCacheValid = (cacheKey: string, expiry: number): boolean => {
    try {
      const state = cache.getCacheState(cacheKey);
      return state.hasCache && state.isFresh;
    } catch (e) {
      return false;
    }
  };

  const getCachedData = <T,>(cacheKey: string): T[] => {
    try {
      const raw = localStorage.getItem('app_cache_v1');
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      const entry = parsed[cacheKey];
      if (entry && entry.data) return entry.data as T[];
    } catch (e) {
      // ignore
    }
    return [];
  };

  // Cache status indicator
  const cacheStatus = useMemo(() => {
    if (loading) return 'Loading...';
    if (isCacheValid(CATEGORIES_CACHE_KEY, CATEGORIES_CACHE_EXPIRY)) return '📚 Using cached data';
    return '🔄 Online data';
  }, [loading, categories.length]);

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    // Filter categories based on search term
    applyFiltersToCachedData();
  }, [searchTerm, categories]);

  useEffect(() => {
    // Ensure current page is in range
    const totalPages = Math.max(1, Math.ceil(filteredCategories.length / itemsPerPage));
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [filteredCategories.length, currentPage]);

  const applyFiltersToCachedData = () => {
    if (searchTerm.trim() === '') {
      setFilteredCategories(categories);
    } else {
      const filtered = categories.filter(category =>
        category.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (category.description && category.description.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setFilteredCategories(filtered);
    }
    setCurrentPage(1); // Reset to first page when searching
  };

  const fetchCategories = async () => {
    setLoading(true);
    try {
      // Use centralized cache; cache.get will return cached data if fresh
      try {
        const data = await cache.get(CATEGORIES_CACHE_KEY, async () => {
          const result = await getLibraryCategories();
          if (!result.success) throw new Error(result.error || 'Failed to fetch categories');
          return result.data;
        }, CATEGORIES_CACHE_EXPIRY);

        const sorted = [...data].sort((a, b) => {
          if (a.createdAt && b.createdAt) {
            return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
          }
          return a.name.localeCompare(b.name);
        });

        setCategories(sorted);
        setFilteredCategories(sorted);
        console.log('🔄 Fetched categories from Firebase and cached');
      } catch (err) {
        console.error('Error fetching categories:', err);
        // Try to fallback to persisted cache
        try {
          const raw = localStorage.getItem('app_cache_v1');
          if (raw) {
            const parsed = JSON.parse(raw);
            const entry = parsed[CATEGORIES_CACHE_KEY];
            if (entry && entry.data) {
              const sorted = [...entry.data].sort((a, b) => {
                if (a.createdAt && b.createdAt) {
                  return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
                }
                return a.name.localeCompare(b.name);
              });
              setCategories(sorted);
              setFilteredCategories(sorted);
              return;
            }
          }
        } catch (e) {
          // ignore
        }
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      // Try to use stale cache if available
      const staleCache = getCachedData<LibraryCategory>(CATEGORIES_CACHE_KEY);
      if (staleCache.length > 0) {
        setCategories(staleCache);
        setFilteredCategories(staleCache);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    // Just apply filters locally, no Firebase call
    applyFiltersToCachedData();
  };

  const handleRefresh = () => {
    // Force refresh by invalidating cache
    invalidateCache(CATEGORIES_CACHE_KEY);
    fetchCategories();
  };

  const handleDeleteCategory = async (categoryId: string) => {
    if (!window.confirm('Are you sure you want to delete this category? Books in this category will need to be reassigned.')) {
      return;
    }
    
    try {
      setLoading(true);
      const result = await deleteLibraryCategory(categoryId);
      if (result.success) {
        // Invalidate cache and refetch
        invalidateCache(CATEGORIES_CACHE_KEY);
        await fetchCategories();
        
        // Also invalidate books cache since books reference categories
        localStorage.removeItem('library_books_cache');
        localStorage.removeItem('attendance_categories_cache');
        
        // Show success message
        const deletedCategory = categories.find(c => c.id === categoryId);
        const message = deletedCategory 
          ? `Category "${deletedCategory.name}" deleted successfully!` 
          : 'Category deleted successfully!';
        alert(message);
      } else {
        alert(`Error: ${result.error}`);
      }
    } catch (error: any) {
      console.error('Error deleting category:', error);
      alert(`Error deleting category: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleAddCategory = () => {
    setEditingCategory(null);
    setShowAddModal(true);
  };

  const handleEditClick = (category: LibraryCategory) => {
    setEditingCategory(category);
    setShowAddModal(true);
  };

  const handleSaveCategory = async (categoryData: { name: string; description?: string; color?: string }) => {
    setLoading(true);
    try {
      const categoryPayload: LibraryCategory = {
        name: categoryData.name.trim(),
        description: categoryData.description?.trim() || '',
        color: categoryData.color || '#3b82f6', // Default blue color
        bookCount: editingCategory?.bookCount || 0,
      };

      if (editingCategory && editingCategory.id) {
        // Update existing category
        const res = await updateLibraryCategory(editingCategory.id, categoryPayload);
        if (!res.success) throw new Error(res.error || 'Update failed');
        alert('Category updated successfully!');
      } else {
        // Create new category
        const res = await createLibraryCategory(categoryPayload);
        if (!res.success) throw new Error(res.error || 'Create failed');
        alert('Category created successfully!');
      }

      // Invalidate cache and refetch
      invalidateCache(CATEGORIES_CACHE_KEY);
      await fetchCategories();
      setShowAddModal(false);
      setEditingCategory(null);
    } catch (error: any) {
      console.error('Save error:', error);
      alert(`Error saving category: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to generate random colors for categories
  const getRandomColor = () => {
    const colors = [
      '#3b82f6', '#10b981', '#8b5cf6', '#f59e0b', '#ef4444',
      '#06b6d4', '#84cc16', '#f97316', '#6366f1', '#ec4899'
    ];
    return colors[Math.floor(Math.random() * colors.length)];
  };

  // Pagination helpers
  const totalPages = Math.max(1, Math.ceil(filteredCategories.length / itemsPerPage));
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const displayedCategories = filteredCategories.slice(startIndex, endIndex);

  return (
    <div className="space-y-6">
      {/* Add Category Modal */}
      {showAddModal && (
        <AddCategoryModal
          isOpen={showAddModal}
          initialData={editingCategory ? {
            name: editingCategory.name,
            description: editingCategory.description,
            color: editingCategory.color
          } : undefined}
          onClose={() => { setShowAddModal(false); setEditingCategory(null); }}
          onSave={handleSaveCategory}
        />
      )}

      {/* Top Controls */}
      <div className="bg-white rounded-xl shadow-soft p-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          {/* Left: Search and Cache Status */}
          <div className="flex flex-wrap items-center gap-3">
            {/* Cache Status */}
            <div className="text-xs text-gray-500 hidden sm:block">
              {cacheStatus}
            </div>

            {/* Search */}
            <div className="flex-1 max-w-md">
              <form onSubmit={handleSearch} className="flex items-center">
                <div className="relative flex-1">
                  <FaSearch className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 h-3 w-3" />
                  <input
                    type="text"
                    placeholder="Search categories by name or description..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="text-sm pl-8 pr-2 py-1 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent w-full"
                  />
                </div>
              </form>
            </div>
          </div>

          {/* Right: Action Buttons */}
          <div className="flex flex-wrap gap-2 items-center">
            {/* Refresh */}
            <button
              onClick={handleRefresh}
              className="flex items-center gap-2 text-sm px-2 py-1 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              title="Refresh from Firebase"
            >
              <FaSync className="h-3 w-3 text-gray-600" />
              <span className="hidden sm:inline">Refresh</span>
            </button>

            {/* Add New */}
            <button
              onClick={handleAddCategory}
              className="flex items-center gap-2 text-sm px-3 py-1 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
              title="Add New Category"
            >
              <FaPlus className="h-3 w-3" />
              <span className="hidden sm:inline">Add Category</span>
            </button>
          </div>
        </div>
      </div>

      {/* Data Grid */}
      <div className="bg-white rounded-xl shadow-soft overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center p-8">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
              <p className="mt-3 text-sm text-gray-600">Loading categories...</p>
            </div>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-xs leading-tight">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-2 py-2 text-left font-medium text-gray-500 uppercase tracking-wide">#</th>
                    <th className="px-2 py-2 text-left font-medium text-gray-500 uppercase tracking-wide">Category Name</th>
                    {/* <th className="px-2 py-2 text-left font-medium text-gray-500 uppercase tracking-wide">Description</th> */}
                    <th className="px-2 py-2 text-left font-medium text-gray-500 uppercase tracking-wide">Color</th>
                    <th className="px-2 py-2 text-left font-medium text-gray-500 uppercase tracking-wide">Book Count</th>
                    <th className="px-2 py-2 text-left font-medium text-gray-500 uppercase tracking-wide">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {displayedCategories.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-6 text-center text-gray-500 text-sm">
                        {categories.length === 0
                          ? 'No categories found. Add your first category!'
                          : 'No categories match your search.'}
                      </td>
                    </tr>
                  ) : (
                    displayedCategories.map((category, index) => (
                      <tr key={category.id} className="hover:bg-gray-50">
                        <td className="px-2 py-2 whitespace-nowrap text-xs text-gray-500 font-medium">
                          {startIndex + index + 1}
                        </td>
                        <td className="px-2 py-2 whitespace-nowrap">
                          <div className="text-xs font-medium text-gray-900">{category.name}</div>
                        </td>
                        {/* <td className="px-2 py-2 whitespace-nowrap">
                          <div className="text-xs text-gray-600 max-w-[200px] truncate">
                            {category.description || 'No description'}
                          </div>
                        </td> */}
                        <td className="px-2 py-2 whitespace-nowrap">
                          <div className="flex items-center space-x-2">
                            <div 
                              className="w-4 h-4 rounded-full border border-gray-300"
                              style={{ backgroundColor: category.color || '#3b82f6' }}
                            />
                            <span className="text-xs text-gray-600 font-mono hidden sm:inline">
                              {category.color || '#3b82f6'}
                            </span>
                          </div>
                        </td>
                        <td className="px-2 py-2 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${
                            (category.bookCount || 0) > 0 
                              ? 'bg-blue-100 text-blue-800' 
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {category.bookCount || 0} books
                          </span>
                        </td>
                        <td className="px-2 py-2 whitespace-nowrap text-xs font-medium">
                          <div className="flex items-center space-x-1">
                            <button
                              onClick={() => handleEditClick(category)}
                              className="text-green-600 hover:text-green-900 p-0.5 rounded hover:bg-green-50"
                              title="Edit"
                            >
                              <FaEdit className="h-3 w-3" />
                            </button>
                            <button
                              onClick={() => category.id && handleDeleteCategory(category.id)}
                              className="text-red-600 hover:text-red-900 p-0.5 rounded hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                              title={category.bookCount ? `Cannot delete: ${category.bookCount} books in this category` : "Delete"}
                              disabled={(category.bookCount || 0) > 0}
                            >
                              <FaTrash className="h-3 w-3" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {filteredCategories.length > itemsPerPage && (
              <div className="px-4 py-3 border-t border-gray-200">
                <div className="flex items-center justify-between text-xs text-gray-700">
                  <div>
                    Showing <span className="font-medium">{filteredCategories.length === 0 ? 0 : startIndex + 1}</span> to{' '}
                    <span className="font-medium">{Math.min(endIndex, filteredCategories.length)}</span> of{' '}
                    <span className="font-medium">{filteredCategories.length}</span> categories
                    <span className="ml-2 text-gray-500">(Total in cache: {categories.length})</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <button
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className={`px-2 py-1 border border-gray-300 rounded text-xs hover:bg-gray-50 ${
                        currentPage === 1 ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                    >
                      Previous
                    </button>

                    {/* Page Numbers */}
                    {Array.from({ length: Math.min(5, totalPages) }).map((_, idx) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = idx + 1;
                      } else if (currentPage <= 3) {
                        pageNum = idx + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + idx;
                      } else {
                        pageNum = currentPage - 2 + idx;
                      }

                      if (pageNum > totalPages) return null;

                      return (
                        <button
                          key={pageNum}
                          onClick={() => setCurrentPage(pageNum)}
                          className={`px-2 py-1 rounded text-xs ${
                            currentPage === pageNum
                              ? 'bg-primary-600 text-white'
                              : 'border border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}

                    {totalPages > 5 && currentPage < totalPages - 2 && (
                      <>
                        <span className="px-2">...</span>
                        <button
                          onClick={() => setCurrentPage(totalPages)}
                          className={`px-2 py-1 border border-gray-300 rounded text-xs hover:bg-gray-50 ${
                            currentPage === totalPages ? 'bg-primary-600 text-white' : ''
                          }`}
                        >
                          {totalPages}
                        </button>
                      </>
                    )}

                    <button
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className={`px-2 py-1 border border-gray-300 rounded text-xs hover:bg-gray-50 ${
                        currentPage === totalPages ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default CategoryManagement;